import type { MeResponse } from "@seagames/contracts";
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { NoAccess } from "../components/auth/no-access";
import { SessionChecking } from "../components/auth/session-screens";
import { AppShell } from "../components/shell/app-shell";
import { ApiError } from "../lib/api/client";
import { type NotionAccess, notionAccessFor } from "../lib/auth/access";
import { SessionUnreachableError, checkSession } from "../lib/auth/session-check";
import { hasStoredSession } from "../lib/auth/token-store";

/**
 * The signed-in shell (`components/shell/app-shell.tsx`). The guard runs in
 * `beforeLoad`, so no page renders before the server has said who is asking;
 * meanwhile the bare band and "Checking your session…" show from the first
 * frame, and a server that does not answer gets the self-retrying screen —
 * never "sign in again" (OVR's guard, copied).
 *
 * Only the SUPER_ADMIN active role gets the shell (`notionAccessFor`);
 * anyone else gets the no-access screen, with "Continue as Super Admin" when
 * they hold it. Route context below: `{ session }`.
 */
export const Route = createFileRoute("/_app")({
	beforeLoad: async ({ context, location }) => {
		if (!hasStoredSession()) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		let session: MeResponse;
		try {
			session = await checkSession(context.queryClient);
		} catch (err) {
			// No answer is not a verdict: retry screen, session intact.
			if (err instanceof ApiError && err.transient) throw new SessionUnreachableError(err);
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		if (session.must_change_password) {
			throw redirect({ to: "/change-password", search: { redirect: location.href } });
		}
		const access: NotionAccess = notionAccessFor(session.active_role);
		return { session, access };
	},
	pendingComponent: SessionChecking,
	pendingMs: 0,
	pendingMinMs: 0,
	component: AppLayout,
});

function AppLayout() {
	const { session, access } = Route.useRouteContext();
	// No <Outlet/> without access, so nothing below mounts or fetches.
	if (access === "none") return <NoAccess session={session} />;
	return (
		<AppShell session={session}>
			<Outlet />
		</AppShell>
	);
}
